/**
 * useIntegrityMonitor (TI-735 + TI-739b).
 *
 * When enabled, runs continuous interview-integrity monitoring on the candidate's local
 * camera track:
 *  - EDGE (every ~1.5s, on-device MediaPipe): face presence + count → instant UX nudges,
 *    no network.
 *  - SERVER (every ~10s): capture a frame → integrity-service /verify-frame → authoritative
 *    identity match + warnings + termination.
 *
 * The reference identity is registered from the first good frame at session start
 * (continuous "same person who started is still present" monitoring). NOTE: the production
 * flow should register against a pre-verified ID/profile photo — that's a TI-249 follow-up.
 *
 * Entirely fail-open and gated by `INTEGRITY_UI_ENABLED`: when off, or on any error, the
 * room is unaffected.
 */

import { useEffect, useState } from "react";

import { INTEGRITY_UI_ENABLED } from "../livekit/endpoints";
import { createEdgeDetector, detectVirtualCamera } from "./edgeSignals";
import { createFrameGrabber } from "./frameCapture";
import {
  interpretEdgeSignal,
  interpretVerifyResponse,
  postEdgeSignals,
  startSession,
  toEdgeSignalEvents,
  verifyFrame,
} from "./integrityClient";

const EDGE_INTERVAL_MS = 1500;
const VERIFY_INTERVAL_MS = 10000;

const IDLE = { active: false, level: "ok", message: null, terminated: false };

export function useIntegrityMonitor({
  mediaStreamTrack,
  candidateId,
  candidateName,
  jwt,
  enabled = INTEGRITY_UI_ENABLED,
}) {
  const [state, setState] = useState(IDLE);

  useEffect(() => {
    if (!enabled || !mediaStreamTrack || !candidateId) return undefined;

    let cancelled = false;
    let edgeTimer = null;
    let verifyTimer = null;
    let started = false;
    const grabber = createFrameGrabber(mediaStreamTrack);
    let detector = null;
    // Edge deviations accumulate here and flush to the server on the verify cadence (~10s),
    // so the cheap on-device signals reach the integrity score without a per-frame POST.
    let edgeBuffer = [];

    setState({ active: true, level: "ok", message: null, terminated: false });

    const flushEdgeSignals = async () => {
      if (cancelled || edgeBuffer.length === 0) return;
      const signals = edgeBuffer;
      edgeBuffer = [];
      try {
        await postEdgeSignals({ candidateId, signals, jwt });
      } catch (_) {
        /* fail-open: drop this batch, keep monitoring */
      }
    };

    const tryStart = async () => {
      if (started || cancelled) return;
      const blob = await grabber.grabBlob();
      if (!blob) return;
      try {
        const res = await startSession({ candidateId, candidateName, blob, jwt });
        if (res && res.ok) started = true;
      } catch (_) {
        /* fail-open */
      }
    };

    (async () => {
      detector = await createEdgeDetector();
      if (cancelled) return;
      await tryStart();

      // One-shot virtual-camera scan: a strong, cheap spoof signal (OBS/Deep-Live-Cam).
      try {
        const vcam = await detectVirtualCamera();
        if (!cancelled && vcam.virtual) {
          edgeBuffer.push({
            type: "virtual_camera",
            timestamp: new Date().toISOString(),
            metadata: { label: vcam.label },
          });
          setState((s) =>
            s.terminated ? s : { ...s, level: "warning", message: "Virtual camera detected" }
          );
        }
      } catch (_) {
        /* fail-open */
      }

      edgeTimer = setInterval(() => {
        if (cancelled || !grabber.isReady() || !detector) return;
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const signal = detector.detect(grabber.videoElement, now);
        edgeBuffer.push(...toEdgeSignalEvents(signal, new Date().toISOString()));
        const nudge = interpretEdgeSignal(signal);
        setState((s) => {
          if (s.terminated || s.level === "critical") return s;
          if (nudge) return { ...s, level: nudge.level, message: nudge.message };
          return { ...s, level: "ok", message: null };
        });
      }, EDGE_INTERVAL_MS);

      verifyTimer = setInterval(async () => {
        if (cancelled) return;
        if (!started) {
          await tryStart();
          return;
        }
        await flushEdgeSignals();
        const blob = await grabber.grabBlob();
        if (!blob) return;
        try {
          const res = await verifyFrame({ candidateId, blob, jwt });
          const body = await res.json().catch(() => null);
          if (cancelled) return;
          const interp = interpretVerifyResponse(body);
          setState((s) => ({
            active: true,
            level: interp.level,
            message: interp.message,
            terminated: interp.terminate || s.terminated,
          }));
        } catch (_) {
          /* fail-open: keep last state */
        }
      }, VERIFY_INTERVAL_MS);
    })();

    return () => {
      cancelled = true;
      if (edgeTimer) clearInterval(edgeTimer);
      if (verifyTimer) clearInterval(verifyTimer);
      try {
        if (detector) detector.dispose();
      } catch (_) {
        /* best-effort */
      }
      grabber.dispose();
      setState(IDLE);
    };
  }, [enabled, mediaStreamTrack, candidateId, candidateName, jwt]);

  return state;
}
